import { FC, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { orderBy } from "lodash-es";

import { Button } from "@/components/button";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { BooleanFieldRow } from "@/components/table/boolean-field-row";
import { AccountSummarySchema } from "@/domains/openapi/types";

interface AccountsTableProps {
  data: AccountSummarySchema[];
  onViewAccount: (id: string) => void;
  pageSize: number;
}

export const AccountsTable: FC<AccountsTableProps> = ({ data, onViewAccount, pageSize }) => {
  const columns = useMemo<ColumnDef<AccountSummarySchema>[]>(
    () => [
      {
        header: "Account ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Name",
        cell: ({ row }) => <TextFieldRow value={row.original.profile_display_name} />,
      },
      {
        header: "Email",
        cell: ({ row }) => <TextFieldRow value={row.original.profile_email_address} />,
      },
      {
        header: "Provisioned At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.provisioned_at} />,
      },
      {
        header: "Google Account?",
        cell: ({ row }) => (
          <BooleanFieldRow value={row.original.has_google_account_authentication ?? null} />
        ),
      },
      {
        header: "Clerk Account?",
        cell: ({ row }) => (
          <BooleanFieldRow value={row.original.has_clerk_account_authentication ?? null} />
        ),
      },
      {
        header: "Soft Deleted?",
        cell: ({ row }) => <BooleanFieldRow value={row.original.is_soft_deleted ?? null} />,
      },
      {
        header: "View",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewAccount(row.original.id)}>
            View
          </Button>
        ),
      },
    ],
    [onViewAccount]
  );

  const sortedData = useMemo(() => {
    return orderBy(
      data,
      item =>
        item.provisioned_at
          ? DateTime.fromISO(item.provisioned_at).toMillis()
          : item.created_at
            ? DateTime.fromISO(item.created_at).toMillis()
            : -Infinity,
      "desc"
    );
  }, [data]);

  return <Table data={sortedData} columns={columns} pageSize={pageSize} />;
};
